import { z } from 'zod'

// =====================
// Reversible merge (T3 — Appendix 1 "Merge mechanics: the asymmetry that governs
// everything"). Under-merging is cheap; over-merging is data corruption — so a
// merge is explicit, conservative, and reversible: B becomes a redirecting
// tombstone pointing at survivor A, never a destructive fuse.
// =====================

// The spine types that support reversible merge.
export enum RecordMergeType {
  CONTACT = 'CONTACT',
  ENTITY = 'ENTITY',
  DEAL = 'DEAL',
}

// Lifecycle marker stored on the record (T2 — Appendix 1 "The lifecycle
// membrane"). Orthogonal to data completeness AND to the funnel lifecycleStage:
//   provisional — real enough to anchor to, but quarantined from authoritative
//                 roll-up (pipeline forecast, margin, health).
//   active      — vouched-for; counts toward everything.
//   merged      — tombstone redirecting to a survivor (T3).
//   archived    — retired; hidden from lists and roll-ups.
export const RECORD_STATE_PROVISIONAL = 'provisional'
export const RECORD_STATE_ACTIVE = 'active'
export const RECORD_STATE_MERGED = 'merged'
export const RECORD_STATE_ARCHIVED = 'archived'
export const RecordStateSchema = z.enum([
  RECORD_STATE_PROVISIONAL,
  RECORD_STATE_ACTIVE,
  RECORD_STATE_MERGED,
  RECORD_STATE_ARCHIVED,
])
export type RecordState = z.infer<typeof RecordStateSchema>

// The T2 active-filter rule (design-rationale.md "The lifecycle membrane"):
//
// - LIST_VISIBLE_RECORD_STATES — what default LIST queries show: active +
//   provisional (provisional is real enough to see and anchor to). Merged /
//   archived tombstones are hidden unless the caller explicitly opts in
//   (admin / merge-journal views). Detail GET-by-id NEVER filters — a
//   tombstone must still resolve so U2 can render its "Merged into X" banner.
// - AUTHORITATIVE_RECORD_STATES — what authoritative aggregations (pipeline
//   forecast, margin roll-up, health scores, funnel analytics) count: active
//   ONLY. Provisional is quarantined from roll-up by design.
export const LIST_VISIBLE_RECORD_STATES: RecordState[] = [
  RECORD_STATE_ACTIVE,
  RECORD_STATE_PROVISIONAL,
]
export const AUTHORITATIVE_RECORD_STATES: RecordState[] = [RECORD_STATE_ACTIVE]

// Opt-in list filter param: default hides tombstones; 'all' shows everything
// (admin/merge-journal); or a single explicit state to filter to exactly it.
export const RecordStateFilterSchema = z.union([
  z.literal('all'),
  RecordStateSchema,
])
export type RecordStateFilter = z.infer<typeof RecordStateFilterSchema>

// Builds the Prisma-shaped where fragment for a list/aggregation query.
// Returns undefined when no constraint should be applied (filter === 'all').
export const buildRecordStateWhere = (
  filter?: RecordStateFilter | null,
): { recordState: string | { in: string[] } } | undefined => {
  if (filter === 'all') return undefined
  if (filter) return { recordState: filter }
  return { recordState: { in: LIST_VISIBLE_RECORD_STATES } }
}

export enum RecordMergeStatus {
  MERGED = 'MERGED',
  REVERSED = 'REVERSED',
}

// Reversibility window: a merge can be un-done for this many days after it lands.
// Conservative by design — the whole point is that an over-merge is recoverable.
export const MERGE_REVERSAL_WINDOW_DAYS = 30

// Survivorship is only ever forced for genuinely singular fields; the caller picks
// which side wins. Naturally multi-valued associations are kept by repointing, not
// by a survivorship choice.
export const MergeFieldWinnerSchema = z.enum(['survivor', 'merged'])
export type MergeFieldWinner = z.infer<typeof MergeFieldWinnerSchema>

export const MergeRequestSchema = z.object({
  recordType: z.nativeEnum(RecordMergeType),
  // A — the record that survives and absorbs B's references.
  survivorId: z.string().uuid(),
  // B — the record that becomes a redirecting tombstone.
  mergedId: z.string().uuid(),
  // Resolutions for conflicting singular fields, e.g. { email: 'survivor' }.
  // Required only when a conflict exists; absence triggers a survivorship prompt.
  fieldWinners: z.record(z.string(), MergeFieldWinnerSchema).optional(),
})
export type MergeRequest = z.infer<typeof MergeRequestSchema>

export const UnmergeRequestSchema = z.object({
  recordMergeId: z.string().uuid(),
})
export type UnmergeRequest = z.infer<typeof UnmergeRequestSchema>

// A singular-field disagreement between survivor and merged record. When any exist
// and the caller has not resolved them, the merge is rejected (never auto-fused).
export interface MergeFieldConflict {
  field: string
  survivorValue: unknown
  mergedValue: unknown
}

// Per-reference move record (also the unit of reversal).
export interface MergeRepointEntry {
  ref: string
  model: string
  fk: string
  movedIds: string[]
}

export interface MergeResult {
  recordMergeId: string
  recordType: RecordMergeType
  survivorId: string
  mergedId: string
  // Reference key -> number of rows moved B -> A.
  repointedCounts: Record<string, number>
}

export interface UnmergeResult {
  recordMergeId: string
  recordType: RecordMergeType
  survivorId: string
  restoredId: string
  restoredCounts: Record<string, number>
}
